import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { MdDelete } from "react-icons/md";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { useBrandHook } from "../useHook";
import Loader, { ImageWithFallback } from "../../../Utils/loader";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";
import { appendImageSeoToFormData } from "../../../Components/Shared/ImageSeoFields";
import BrandModal from "./component/BrandModal";

function BrandManagement() {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.BrandsManagement.CREATE);
  const canUpdate = hasPermission(SystemPermissions.BrandsManagement.UPDATE);
  const canDelete = hasPermission(SystemPermissions.BrandsManagement.DELETE);

  const { getBrands, addBrand, updateBrand, deleteBrand } = useBrandHook();

  const [brands, setBrands] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [mainLoading, setMainLoading] = useState(true);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setMainLoading(true);
      await getBrands(setBrands);
    } catch (error) {
      console.error("Error fetching brands:", error);
    } finally {
      setMainLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingBrand(null);
    setIsModalOpen(true);
  };

  const openEditModal = (brand) => {
    setEditingBrand(brand);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingBrand(null);
  };

  const handleSave = async (payload) => {
    try {
      const fd = new FormData();
      if (payload.file) {
        fd.append("image", payload.file);
      }
      fd.append("name", payload.name);
      if (payload.brandName_ur) {
        fd.append("brandName_ur", payload.brandName_ur);
      }
      appendImageSeoToFormData(fd, payload.image_alt, payload.image_title);

      if (editingBrand) {
        await updateBrand(editingBrand.id, fd);
        Swal.fire("Success!", "Brand updated successfully.", "success");
      } else {
        await addBrand(fd);
        Swal.fire("Success!", "Brand added successfully.", "success");
      }

      await fetchBrands();
      closeModal();
    } catch (error) {
      Swal.fire(
        "Error!",
        error?.message ||
          (editingBrand ? "Failed to update brand." : "Failed to add brand."),
        "error",
      );
    }
  };

  const handleDeleteBrand = (index) => {
    const brandId = brands[index].id;
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setDeletingIndex(index);
          await deleteBrand(brandId);
          Swal.fire("Deleted!", "Brand has been deleted.", "success");
          await fetchBrands();
        } catch (error) {
          Swal.fire("Error!", error?.message || "Failed to delete brand.", "error");
        } finally {
          setDeletingIndex(null);
        }
      }
    });
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;

    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      return imageUrl;
    }

    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    return imageUrl.startsWith("/")
      ? `${baseUrl}${imageUrl}`
      : `${baseUrl}/${imageUrl}`;
  };

  return (
    <div className="bg-gray-100 p-4 min-h-screen">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold">Brand Management</h2>

        {canCreate && (
          <button
            className="btn btn-primary h-[44px]"
            onClick={openAddModal}
          >
            <FiPlus className="text-white" />
            Add Brand
          </button>
        )}
      </div>

      {mainLoading && (
        <Loader message="Loading brands..." />
      )}

      {!mainLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {(Array.isArray(brands) ? brands : []).map((brand, index) => (
            <div
              key={brand.id || index}
              className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md"
            >
              <div className="flex h-[250px] w-full items-center justify-center overflow-hidden border-b bg-gray-50 p-4">
                <ImageWithFallback
                  src={getImageUrl(brand.image)}
                  alt={`${brand.name} logo`}
                  className="max-h-[250px] max-w-[250px] w-full h-full object-contain"
                />
              </div>

              <div className="flex flex-1 flex-col p-4">
                <h3 className="mb-3 truncate text-center text-lg font-semibold text-gray-800" title={brand.name}>
                  {brand.name}
                </h3>

                <div className="mt-auto flex gap-2">
                  {canUpdate && (
                    <button
                      className="btn btn-edit text-white flex-1 justify-center"
                      onClick={() => openEditModal(brand)}
                    >
                      <FiEdit2 className="text-white" /> Edit
                    </button>
                  )}
                  {canDelete && (
                    <button
                      className={`btn btn-delete text-white flex-1 justify-center ${
                        deletingIndex === index
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      onClick={() => handleDeleteBrand(index)}
                      disabled={deletingIndex === index}
                    >
                      {deletingIndex === index ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <MdDelete className="text-white" /> Delete
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!mainLoading && brands?.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No brands yet</h3>
          <p className="text-gray-500 mb-6">Get started by adding your first brand</p>
        </div>
      )}

      <BrandModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editingBrand={editingBrand}
        onSubmit={handleSave}
      />
    </div>
  );
}

export default BrandManagement;
